import { useQuery } from '@apollo/client'
import { Badge, IconButton, Tooltip } from '@material-ui/core'
import SupportIcon from '@material-ui/icons/HeadsetMic'
import { useHistory } from 'react-router-dom'

import { TICKET_BADGE_COUNT_QUERY } from '../../graphql/tickets'
import { needsAction, Ticket, TicketList } from '../../helpers/ticketWorkflow'
import { getTranslate, lang } from '../../localization'

/**
 * How many of the caller's own tickets to inspect for the badge.
 *
 * There is NO count field on the tickets query — it returns a page plus a
 * nextPageToken — so the badge counts open tickets within one page rather than
 * across all of them. 50 is chosen to make that indistinguishable from an exact
 * count for anyone with a normal number of open tickets, and `countOpenTickets`
 * below is explicit about what happens past it.
 *
 * The honest fix is a count field on the BFF. Until then the badge is allowed
 * to say "at least this many", never a wrong precise number.
 */
const BADGE_SAMPLE_SIZE = 50

interface OpenTicketCount {
  count: number
  /** True when more tickets exist than were inspected — display as "N+". */
  atLeast: boolean
}

/**
 * Counts non-terminal tickets in a page, and reports whether the count is a
 * floor rather than a total.
 *
 * Exported for the test: the distinction between "3 open" and "at least 3 open"
 * is the whole reason this is not a one-line filter at the call site.
 */
export function countOpenTickets(
  tickets: Ticket[] | undefined,
  hasMorePages: boolean
): OpenTicketCount {
  // needsAction, not !isTerminal. isTerminal answers "is this over" (CLOSED
  // only) and is right for dimming a row; the badge is asking "is there
  // anything to do", and a RESOLVED ticket has nothing outstanding — the work
  // is done and it is waiting to be closed. Counting it lit the badge over an
  // empty queue with no way to see what it referred to.
  const open = (tickets ?? []).filter((ticket) => needsAction(ticket.state))

  return { count: open.length, atLeast: hasMorePages }
}

/**
 * Top-bar entry to support, mirroring NotificationBell.
 *
 * Its reason to exist is reporting a problem from wherever the problem is: the
 * drawer entry requires knowing support is in the drawer, and someone who has
 * just hit a broken screen should not have to go looking.
 */
export default function SupportButton() {
  const history = useHistory()

  const { data, error } = useQuery<{ tickets: TicketList }>(
    TICKET_BADGE_COUNT_QUERY,
    {
      variables: { tab: 'MINE', pageSize: BADGE_SAMPLE_SIZE },
      fetchPolicy: 'cache-and-network',
      // The badge is an affordance, not a report: a failed count must never
      // surface a toast over whatever the user was actually doing. But suppressed
      // is not the same as unobserved — a badge that has been silently failing
      // for weeks looks exactly like a user with no open tickets. Logged so the
      // difference is visible somewhere.
      //
      // Auth failures are NOT what this handles: apolloClient's logoutLink runs
      // earlier in the link chain and redirects on session expiry regardless of
      // this callback. What lands here is a transient BFF error.
      onError: (queryError) =>
        // eslint-disable-next-line no-console
        console.warn(
          'Support ticket badge count unavailable:',
          queryError.message
        )
    }
  )

  const { count, atLeast } = countOpenTickets(
    data?.tickets.tickets,
    Boolean(data?.tickets.nextPageToken)
  )

  // One sentence, used for BOTH the tooltip and the accessible name.
  //
  // They used to differ: the aria-label said "Support, 4 open tickets" while
  // the tooltip said only "Support", so a sighted user hovering the badge saw a
  // number and no way to learn what it counted — reported as exactly that.
  // A screen-reader user was better served than someone looking at it.
  //
  // The duplication this creates is KNOWN AND ACCEPTED. MUI sets the child's
  // `title` while the tooltip is closed and `aria-describedby` while it is
  // open, so a screen reader announces the sentence as both name and
  // description. Identical text is the safe case: the failure modes of a
  // name/description pair are contradiction and silent divergence, and this has
  // neither. Every way to de-duplicate in v4 reintroduces two sources of truth,
  // which is the bug being fixed. Do not "fix" this into divergence.
  //
  // `error` is a separate branch, not folded into the zero case. `data` is
  // undefined while loading and after a failed fetch, so count is 0 in three
  // different situations — unknown, broken, and genuinely empty. Asserting
  // "no open tickets" for all three was survivable while it lived only in the
  // aria-label; putting it in the tooltip would have shown a confident false
  // zero to everyone, for the whole duration of an outage.
  const label = error
    ? getTranslate('Support, ticket count unavailable')
    : count === 0
    ? getTranslate('Support, no open tickets')
    : atLeast
    ? count === 1
      ? getTranslate('Support, at least 1 open ticket')
      : getTranslate('Support, at least {count} open tickets', { count })
    : count === 1
    ? getTranslate('Support, 1 open ticket')
    : getTranslate('Support, {count} open tickets', { count })

  return (
    <Tooltip title={label}>
      <IconButton
        color="inherit"
        // Same string. If these ever diverge again, the one a sighted user can
        // reach is the one that goes stale, because nothing renders it visibly
        // in a test.
        aria-label={label}
        onClick={() => history.push(`/${lang}/support`)}>
        {/* "N+" is written directly rather than squeezed out of `max`. An
            earlier version set max={count - 1} to make MUI append the "+",
            which rendered "49+" for 50 known-open tickets — truthful, off by
            one, and not what its own comment claimed. max={99} stays for the
            ordinary case, matching NotificationBell. */}
        {/* dir="ltr" on the "N+" form. `+` is bidi-neutral, so trailing a
            European numeral inside an RTL paragraph it takes paragraph
            direction and renders to the LEFT of the digits — a Farsi reader
            sees "+50", which reads as a signed number rather than "at least".
            The plain count needs no wrapper; a bare numeral has no neutral to
            misplace. */}
        <Badge
          badgeContent={atLeast ? <span dir="ltr">{`${count}+`}</span> : count}
          color="secondary"
          max={99}>
          <SupportIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  )
}
